import './styles/global.css';
import '@trucktrax/trucktrax-common/src/styles/components/tt_fonts.css';
import '@trucktrax/trucktrax-common/src/styles/tt_icomoon.css';

import { LoadScript } from '@react-google-maps/api';
import { Loader } from '@trucktrax/trucktrax-common';
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import config from 'react-global-configuration';
import { Provider } from 'react-redux';

import { CONFIG_URL } from './constants/appConstants';
import Main, { store } from './Main';
import { useTracer } from './telemetry/Tracer';
import recordLog, { LogLevel } from './util/errorUtil';

config.set({}, { freeze: false, assign: false });

function TracerInitializer({ isInitialized }: { isInitialized: (value: boolean) => void }) {
  const tracer = useTracer();
  const initializedRef = React.useRef(false);
  React.useEffect(() => {
    if (tracer && !initializedRef.current) {
      isInitialized(true);
      initializedRef.current = true;
      recordLog(LogLevel.Debug, '[TracerInitializer] initialized tracer successfully!');
    }
  }, [tracer, isInitialized]);
  return null;
}

function handleNewConfiguration(configObject: any): void {
  config.set(configObject, { assign: true, freeze: false });
}

function AppInitializer(): JSX.Element {
  const [tracerInitialized, setTracerInitialized] = useState(false);

  if (!tracerInitialized) {
    return <TracerInitializer isInitialized={setTracerInitialized} />;
  }
  return <Main />;
}

function AppWrapper(): JSX.Element {
  const [configLoaded, setConfigLoaded] = useState(false);

  // Step 1: Load runtime configuration first
  if (!configLoaded) {
    return (
      <Loader
        configPath={CONFIG_URL}
        onNewConfiguration={handleNewConfiguration}
        onLoadingCompletion={() => setConfigLoaded(true)}
      />
    );
  }

  // Step 2: Check for Google Maps API key from runtime config (or build-time fallback)
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_API_KEY || config.get('REACT_APP_GOOGLE_API_KEY');
  const googleMapId = process.env.REACT_APP_GOOGLE_MAP_ID || config.get('REACT_APP_GOOGLE_MAP_ID');

  // Log configuration errors for observability
  if (!googleMapsApiKey) {
    recordLog(LogLevel.Error, 'REACT_APP_GOOGLE_API_KEY is missing from configuration', {
      processEnvKeyExists: !!process.env.REACT_APP_GOOGLE_API_KEY,
      configKeyExists: !!config.get('REACT_APP_GOOGLE_API_KEY'),
    });
  }

  // Step 3: Show error if API key is missing
  if (!googleMapsApiKey) {
    return <MapsLoadError apiKeyPresent={false} mapIdPresent={!!googleMapId} />;
  }

  // Step 4: Load Google Maps with actual API key from runtime config
  return (
    <LoadScript
      googleMapsApiKey={googleMapsApiKey}
      libraries={['drawing', 'geometry', 'places', 'marker']}
      version="weekly"
      loadingElement={<div>Loading Google Maps...</div>}
      {...(googleMapId ? { mapIds: [googleMapId] } : {})}
      onLoad={() => recordLog(LogLevel.Info, 'Google Maps loaded successfully')}
      onError={err => {
        recordLog(LogLevel.Error, 'Failed to load Google Maps', { error: err });
        const mount = document.getElementById('root');
        if (mount) {
          const fallbackRoot = ReactDOM.createRoot(mount);
          fallbackRoot.render(
            <MapsLoadError apiKeyPresent={!!googleMapsApiKey} mapIdPresent={!!googleMapId} />,
          );
        }
      }}
    >
      {/* @ts-ignore */}
      <Provider store={store}>
        {/* @ts-ignore */}
        <AppInitializer />
      </Provider>
    </LoadScript>
  );
}

function MapsLoadError({
  apiKeyPresent,
  mapIdPresent,
}: {
  apiKeyPresent: boolean;
  mapIdPresent: boolean;
}) {
  return (
    <div style={{ fontFamily: 'sans-serif', padding: 24 }}>
      <h2>Google Maps failed to load</h2>
      <p>Check the following in your environment:</p>
      <ul>
        <li>
          <strong>REACT_APP_GOOGLE_API_KEY</strong>{' '}
          <span>{apiKeyPresent ? 'present' : 'MISSING'}</span>
        </li>
        <li>
          <strong>REACT_APP_GOOGLE_MAP_ID</strong>{' '}
          <span>{mapIdPresent ? 'present' : 'not set'}</span>
        </li>
      </ul>
      <p>
        See browser console for details. Ensure the Maps JavaScript API and Distance Matrix API are
        enabled and the key is authorized for your domain.
      </p>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<AppWrapper />);
